import { useFormContext, useWatch } from 'react-hook-form';
import { useSelectPackageContext } from '../../PackageSelectionForm/selectPackageContext';
import { useRootSelector } from '../../../../../../../store/hooks';
import { selectPricingBundledProductsMap } from '../../../../../store/features/products';
import { getSingleProductIdsList } from '../../../../../helpers/selectPackage/getPreselectedPackageFormValues';
import { canIncludeBundledProductToPackage } from '../../../../../helpers/selectPackage/canIncludeBundledProductToPackage';
import { PricingProductType } from '../../../../../store/features/products/types';
import { PricingProductSelectionTypes } from '../../../../../types/enums';
import {
  ProductsSelections,
  PricingPackagesFormValues,
} from '../../../../../store/features/packages/types';
import SecondLevelListWrapper from '../shared/SecondLevelListWrapper';
import ListHeading from './ListHeading';
import SelectionResetButton from '../shared/SelectionResetButton';
import MultipleProductsList from './MultipleProductsList';
import SingleProductsList from './SingleProductsList';

export interface SecondaryProductsProps
  extends Pick<
    PricingProductType,
    'products' | 'name' | 'selectionType' | 'productTypeId'
  > {}

export default function SecondaryProducts(props: SecondaryProductsProps) {
  const { products, name, selectionType, productTypeId } = props;
  const singleFieldName = 'productsSelections.productSingleIdsMap';
  const multipleFieldName = 'productsSelections.productMultipleIds';

  const { setValue } = useFormContext<PricingPackagesFormValues>();
  const productSingleIdsMap = useWatch<PricingPackagesFormValues>({
    name: singleFieldName,
  }) as ProductsSelections['productSingleIdsMap'];
  const productSingleValue = useWatch<PricingPackagesFormValues>({
    name: `${singleFieldName}.${productTypeId}`,
  }) as string;
  const productMultipleIds = useWatch<PricingPackagesFormValues>({
    name: multipleFieldName,
  }) as string[];
  const { handleChangeSelections } = useSelectPackageContext();

  const bundledProductsMap = useRootSelector(selectPricingBundledProductsMap);

  const productSingleIds = getSingleProductIdsList(productSingleIdsMap);
  const allProductIds = [...productMultipleIds, ...productSingleIds];

  const isSingleChoice =
    selectionType === PricingProductSelectionTypes.SINGLE_CHOICE;
  const isBundledProductAddable = canIncludeBundledProductToPackage(
    productSingleValue,
    allProductIds,
    bundledProductsMap,
  );
  const isResetButton =
    isSingleChoice && productSingleValue && isBundledProductAddable;

  const handleResetSingleProductSelections = () => {
    setValue(`${singleFieldName}.${productTypeId}`, '');
    handleChangeSelections();
  };

  return (
    <SecondLevelListWrapper
      heading={
        <ListHeading title={name}>
          {isResetButton && (
            <SelectionResetButton
              onClick={handleResetSingleProductSelections}
            />
          )}
        </ListHeading>
      }
      className="informa-payment-secondary-products"
    >
      {isSingleChoice ? (
        <SingleProductsList products={products} productTypeId={productTypeId} />
      ) : (
        <MultipleProductsList products={products} />
      )}
    </SecondLevelListWrapper>
  );
}
